import SearchInput from "components/inputs/search-input";
import Typography from "components/typography";
import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { PromotionType, Station, SelectedPackage } from "types/promoType";
import { Colors } from "utils/colors";
import { RiShoppingCart2Line } from "react-icons/ri";
import StationItem from "./stationItem";
import { StationPlaceHolderData } from "./data";

interface IProps {
  types: PromotionType[];
  cartItems: SelectedPackage[];
  setCartItems: (item: SelectedPackage) => void;
  setStep: (step: number) => void;
}
const StepThree = ({ types, cartItems, setCartItems, setStep }: IProps) => {
  const mediaTypesArray = ["Radio", "Television", "Press"];
  const [activeMedia, setActiveMedia] = useState(mediaTypesArray[0]);

  const [selectedPackages, setSelectedPackages] = useState<SelectedPackage[]>(
    []
  );

  const OnSelectPackage = (station: Station, packageId: string) => {
    const packageData = station.packages.find((pkg) => pkg.id === packageId);
    if (!packageData) return;

    // Check if this station already has a package in the cart
    const isStationInCart = cartItems.some(
      (item) => item.stationId === `${station.name}-${station.location}`
    );

    // If station is already in cart, don't allow selection
    if (isStationInCart) return;

    const newSelectedPackage: SelectedPackage = {
      stationId: `${station.name}-${station.location}`,
      stationName: station.name,
      stationType: station.type,
      packageId: packageId,
      packageName: packageData.name,
      packagePrice: packageData.price
    };

    setSelectedPackages((prev) => {
      // Check if this exact package is already selected
      const isAlreadySelected = prev.some(
        (pkg) =>
          pkg.stationId === newSelectedPackage.stationId &&
          pkg.packageId === newSelectedPackage.packageId
      );

      if (isAlreadySelected) {
        // Remove the package if already selected
        return prev.filter(
          (pkg) =>
            !(
              pkg.stationId === newSelectedPackage.stationId &&
              pkg.packageId === newSelectedPackage.packageId
            )
        );
      } else {
        // Remove any existing package from this station and add the new package
        const filteredPackages = prev.filter(
          (pkg) => pkg.stationId !== newSelectedPackage.stationId
        );
        return [...filteredPackages, newSelectedPackage];
      }
    });
  };

  const isPackageSelected = (station: Station, packageId: string) => {
    // Check if package is selected in the current selection
    const isSelectedInCurrent = selectedPackages.some(
      (pkg) =>
        pkg.stationId === `${station.name}-${station.location}` &&
        pkg.packageId === packageId
    );

    // Check if package is already in the cart
    const isInCart = cartItems.some(
      (item) =>
        item.stationId === `${station.name}-${station.location}` &&
        item.packageId === packageId
    );

    return isSelectedInCurrent || isInCart;
  };

  const getSelectedPackagesForStation = (station: Station) => {
    return selectedPackages.filter(
      (pkg) => pkg.stationId === `${station.name}-${station.location}`
    );
  };

  const onAddToCart = (
    station: Station,
    stationSelectedPackages: SelectedPackage[]
  ) => {
    // Add all selected packages for this station to the cart
    stationSelectedPackages.forEach((packageItem) => {
      setCartItems(packageItem);
    });

    // Clear the selected packages for this station after adding to cart
    setSelectedPackages((prev) =>
      prev.filter(
        (pkg) => pkg.stationId !== `${station.name}-${station.location}`
      )
    );
  };

  const [cartSize, setCartSize] = useState(0);

  useEffect(() => {
    setCartSize(cartItems.length);
  }, [cartItems]);

  return (
    <div>
      <Header>
        <MediaTypeContainer>
          {mediaTypesArray.map((media, i) => {
            let status = "disabled";

            if (media === "Radio" && types.includes("Radio Promotion")) {
              status = "available";
            }
            if (media === "Television" && types.includes("TV Promotion")) {
              status = "available";
            }

            if (media === "Press" && types.includes("Blog/Press Release")) {
              status = "available";
            }

            if (activeMedia === media) {
              status = "active";
            }
            return (
              <MediaType
                key={i}
                onClick={() => {
                  if (status === "available") setActiveMedia(media);
                }}
                style={{
                  cursor: status === "availbale" ? "pointer" : "not-allowed"
                }}
              >
                <Typography
                  weight={status === "disabled" ? "" : "medium"}
                  size="lg"
                  color={
                    status === "active"
                      ? "white"
                      : status === "available"
                      ? Colors.grey500
                      : Colors.grey600
                  }
                >
                  {media}
                </Typography>
              </MediaType>
            );
          })}
        </MediaTypeContainer>
        <SearchContainer>
          <SearchInput
            placeholder={
              activeMedia === "Radio"
                ? "Search for radio stations"
                : activeMedia === "Television"
                ? "Search for Tv stations"
                : "Search for Press stations"
            }
          />
        </SearchContainer>
        <CartContainer
          onClick={() => {
            if (cartItems.length) setStep(4);
          }}
          style={{ cursor: cartItems.length > 0 ? "pointer" : "not-allowed" }}
        >
          <Typography size="xl" color={Colors.grey400} m_display="none">
            Cart
          </Typography>
          <CartIcon>
            {cartSize ? (
              <CartBadge>
                <Typography size="xs">{cartSize}</Typography>
              </CartBadge>
            ) : (
              <></>
            )}

            <RiShoppingCart2Line size={16} color={Colors.grey400} />
          </CartIcon>
        </CartContainer>
      </Header>
      <StationsGrid>
        {StationPlaceHolderData[
          activeMedia as keyof typeof StationPlaceHolderData
        ].map((station: Station, index: number) => {
          return (
            <StationItem
              key={index}
              station={station}
              onSelectPackage={OnSelectPackage}
              isPackageSelected={isPackageSelected}
              selectedPackages={getSelectedPackagesForStation(station)}
              onAddToCart={onAddToCart}
            />
          );
        })}
      </StationsGrid>
    </div>
  );
};

export default StepThree;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.4rem 0;

  @media only screen and (max-width: 769px) {
    flex-wrap: wrap;
  }
`;

const MediaTypeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  @media only screen and (max-width: 769px) {
    width: 100%;
    gap: 6rem;
    justify-content: center;
    margin-bottom: 1.6rem;
  }
`;

const MediaType = styled.div``;

const SearchContainer = styled.div`
  width: 50rem;
  @media only screen and (max-width: 769px) {
    width: 85%;
  }
`;

const CartContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CartIcon = styled.div`
  position: relative;
`;

const CartBadge = styled.div`
  padding: 0.2rem 0.55rem;
  border-radius: 1000px;
  background-color: ${Colors.danger};
  position: absolute;
  top: -25%;
  right: -60%;
`;

const StationsGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  column-gap: 1.6rem;
  row-gap: 1.6rem;
  margin-top: 2.4rem;
  @media only screen and (max-width: 769px) {
    grid-template-columns: auto;
  }
`;
